import React, { useState, useCallback } from 'react';
import { BaseAutocomplete } from '@askporter/component-library/BaseAutocomplete';
import { BaseAutocompleteValue } from '@askporter/component-library/RevampedAutocomplete/types';
import { SxProps, Theme } from '@mui/material/styles';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SearchTeamFilter, TeamSearchResult } from '@askporter/client-grieg-lyric';
import { queryKeys } from '@askporter/query-keys';
import { useDebounce } from '@askporter/utils';
import doTeamSearch from './utils/doTeamSearch';
type MultipleTeamPropUnion =
  | {
      /**
       * The selected team for the autocomplete component
       */
      value: TeamSearchResult;
      /**
       * Whether the autocomplete allows multiple teams to be selected
       */
      isMultiple?: false;
      /**
       * The function to call when the selected value changes (e.g. A team option is selected)
       */
      onChange: (team: TeamSearchResult) => void;
    }
  | {
      /**
       * The selected teams for the autocomplete component
       */
      value: TeamSearchResult[];
      /**
       * Whether the autocomplete allows multiple teams to be selected
       */
      isMultiple: true;
      /**
       * The function to call when the selected value changes(e.g. A team option is selected)
       */
      onChange: (team: TeamSearchResult[]) => void;
    };
export type TeamAutocompleteProps = {
  /**
   * The translation function
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * The prefix for the id for the autocomplete component
   */
  idPrefix: string;
  /**
   * The label for the autocomplete component
   */
  label: string;
  /**
   * Whether the autocomplete component is disabled
   */
  disabled?: boolean;
  /**
   * The function to determine whether to show the user in the list of users returned in the search request
   */
  showTeam?: (user: TeamSearchResult) => boolean;
  /**
   * The filter to apply to the search request
   */
  filter?: SearchTeamFilter;
  /**
   * Whether there is an error which should be displayed
   */
  error?: boolean;
  /**
   * The helper text to display
   */
  helperText?: string;
  /**
   * The adornment to display at the top of the list (e.g. create an unregistered user button)
   */
  topOfListAdornment?: React.ReactNode;
  /**
   * Optional function to call when the input value changes (e.g. when the user types in the input field)
   */
  onInputChange?: (newInputValue: string) => void;
  /**
   * The function to close the autocomplete drawer, primary used on small devices
   */
  closeComponent?: {
    shouldClose: boolean;
    reset: () => void;
  };
  /**
   * Whether the autocomplete component is read only
   */
  readOnly?: boolean;
  /**
   * The text to display when the autocomplete component is read only
   */
  readOnlyText?: string;
  /**
   * Optional to set the input to use full width
   */
  fullWidth?: boolean;
  /**
   * Optional sx for the autocomplete component
   */
  sx?: SxProps<Theme>;
  /**
   * The page size to use for the search request
   */
  pageSize?: number;
} & MultipleTeamPropUnion;
/**
 * An autocomplete component for selecting a team for a list of options with infinite scroll,
 * it hits `teams/search` endpoint to get the list of teams
 */
export const TeamAutocomplete = ({
  idPrefix,
  label,
  disabled,
  filter = {},
  error = false,
  helperText,
  showTeam = () => true,
  topOfListAdornment,
  onInputChange,
  closeComponent,
  readOnly = false,
  readOnlyText = undefined,
  t,
  fullWidth = false,
  sx,
  pageSize,
  isMultiple,
  value,
  onChange,
}: TeamAutocompleteProps): JSX.Element => {
  const [inputValue, setInputValue] = useState('');
  const teamKeyword = useDebounce<string>(inputValue, 1000);
  const search = { freeText: teamKeyword, filter, pageSize };
  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, status } = useInfiniteQuery({
    queryKey: queryKeys.people.teams.search({ search }),
    queryFn: async ({ pageParam }) => {
      const response = await doTeamSearch({
        ...search,
        page: pageParam,
      });
      return { response, nextPage: pageParam + 1 };
    },
    initialPageParam: 1,
    enabled: teamKeyword === '' || teamKeyword?.length > 2,
    getNextPageParam: (data) => {
      return data?.response?.summary?.totalPages >= data?.nextPage ? data?.nextPage : undefined;
    },
  });
  const mapTeamToBaseAutocompleteValue = useCallback((team: TeamSearchResult): BaseAutocompleteValue => {
    return {
      uid: team.uid,
      label: team.name,
    };
  }, []);
  const opts: TeamSearchResult[] =
    data?.pages && data?.pages.length > 0
      ? data.pages.reduce((acc, itm) => acc.concat([...itm.response.results.filter((team) => showTeam(team))]), [])
      : [];
  const additionalOptions = isMultiple ? value : [value];
  // If value is undefined or null, it will be included in the options array but we don't want that
  const teamOptions = [...additionalOptions, ...opts]
    .filter((team): team is TeamSearchResult => Boolean(team))
    // Combine search results and selected values, removing any duplicates (by uid)
    .reduce<TeamSearchResult[]>((unique, team) => {
      if (!unique.some((t) => t.uid === team.uid)) {
        unique.push(team);
      }
      return unique;
    }, []);
  // Map to BaseAutocompleteValue
  const options: BaseAutocompleteValue[] = teamOptions.map(mapTeamToBaseAutocompleteValue);
  // Map current value to BaseAutocompleteValue
  const mappedValue = isMultiple
    ? (value as TeamSearchResult[]).map(mapTeamToBaseAutocompleteValue)
    : value
    ? mapTeamToBaseAutocompleteValue(value as TeamSearchResult)
    : null;
  // Determine if we should show topOfListAdornment - it should appear when there are search results
  const shouldShowTopAdornment = opts.length > 0 && topOfListAdornment;
  // Handle onChange - only called when option is clicked
  const handleChange = (newValue: BaseAutocompleteValue | BaseAutocompleteValue[] | null) => {
    if (isMultiple) {
      const newValues = (newValue as BaseAutocompleteValue[]) || [];
      const teamValues = newValues
        .map((v) => teamOptions.find((t) => t.uid === v.uid))
        .filter((t): t is TeamSearchResult => Boolean(t));
      (onChange as (teams: TeamSearchResult[]) => void)(teamValues);
    } else {
      const newVal = newValue as BaseAutocompleteValue | null;
      const teamValue = newVal ? teamOptions.find((t) => t.uid === newVal.uid) : null;
      if (teamValue) {
        setInputValue(teamValue.name);
      } else {
        setInputValue('');
      }
      (onChange as (team: TeamSearchResult) => void)(teamValue as TeamSearchResult);
    }
  };
  // Handle input change - native behavior: only update input value
  const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    onInputChange && onInputChange(newInputValue);
  };
  const baseProps = {
    t,
    idPrefix,
    label,
    placeholder: '',
    disabled,
    error,
    helperText,
    options,
    showLabel: true,
    onInputChange: handleInputChange,
    inputValue,
    topOfListAdornment: shouldShowTopAdornment ? topOfListAdornment : undefined,
    loading: status === 'pending' || isFetchingNextPage,
    onScrollToBottom: () => {
      if (hasNextPage) {
        fetchNextPage();
      }
    },
    mapOptionToValue: (option: unknown) => {
      const baseValue = option as BaseAutocompleteValue;
      return {
        uid: baseValue.uid,
        label: baseValue.label || '',
      };
    },
    readOnly,
    readOnlyText,
    isListAvatarVisible: false,
    fullWidth,
    filterOptions: (x: BaseAutocompleteValue[]) => x,
    closeComponent,
    sx,
    clearable: true,
    freeSolo: true,
  };
  if (isMultiple) {
    return (
      <BaseAutocomplete
        {...baseProps}
        multiple={true}
        value={mappedValue as BaseAutocompleteValue[]}
        onChange={handleChange as (value: BaseAutocompleteValue[]) => void}
      />
    );
  }
  return (
    <BaseAutocomplete
      {...baseProps}
      multiple={false}
      value={mappedValue as BaseAutocompleteValue | null}
      onChange={handleChange as (value: BaseAutocompleteValue | null) => void}
    />
  );
};
